/**
 * Labstep
 *
 * @module components/Chemical/Form/ShowEdit/Inventory
 * @desc ShowEditInventory for Chemical
 */

import ChemicalActionLinkToInventory from 'labstep-web/components/Chemical/Action/LinkToInventory';
import { ChemicalFormShowEditInventoryMultipleResources } from 'labstep-web/components/Chemical/Form/ShowEdit/Inventory/MultipleResources';
import { ChemicalFormShowEditInventoryNoResourcesContainer as ChemicalFormShowEditInventoryNoResources } from 'labstep-web/components/Chemical/Form/ShowEdit/Inventory/NoResources';
import { ProtocolValuePreviewInline } from 'labstep-web/components/ProtocolValue/Preview/Inline';
import { EntityUpdateContainer } from 'labstep-web/containers/Entity/Update';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import type { Resource } from 'labstep-web/models/resource.model';
import type React from 'react';
import { useState } from 'react';
import type {
  ChemicalFormShowEditInventoryContainerProps,
  ChemicalFormShowEditInventoryProps,
} from './types';

export const typeNoResources = 'no_resources';
export const typeMultipleResources = 'multiple_resources';

export const ChemicalFormShowEditInventory: React.FC<
  ChemicalFormShowEditInventoryProps
> = ({
  chemical,
  status,
  update,
  showActionLinkToInventory,
  isTemplate,
}) => {
  const [modalType, setModalType] = useState('');
  const [resources, setResources] = useState<Resource[]>([]);

  const reset = () => {
    setModalType('');
    setResources([]);
  };

  let header = null;
  let content = null;
  switch (modalType) {
    case typeNoResources:
      header = 'No resources found';
      content = (
        <ChemicalFormShowEditInventoryNoResources
          chemical={chemical}
          update={update}
          options={{ onSuccess: reset }}
        />
      );
      break;
    case typeMultipleResources:
      header = 'Multiple resources found';
      content = (
        <ChemicalFormShowEditInventoryMultipleResources
          status={status}
          update={update}
          options={{ onSuccess: reset }}
          resources={resources}
        />
      );
      break;
    default:
      break;
  }

  return chemical.protocol_value.resource ? (
    <ProtocolValuePreviewInline
      protocolValue={chemical.protocol_value}
      withResourceItem={!isTemplate}
    />
  ) : (
    <>
      <ModalDefault
        open={!!modalType}
        viewComponent={null}
        onClose={reset}
        header={header}
        content={content}
      />
      {showActionLinkToInventory ? (
        <ChemicalActionLinkToInventory
          chemical={chemical}
          disabled={(status && status.isFetching) || false}
          onFetchEntities={(entities) => {
            if (entities.length === 1) {
              update({ resource_id: entities[0].id });
              reset();
            } else {
              setResources(entities as Resource[]);
              setModalType(
                entities.length > 1
                  ? typeMultipleResources
                  : typeNoResources,
              );
            }
          }}
        />
      ) : (
        'No inventory'
      )}
    </>
  );
};

export const ChemicalFormShowEditInventoryContainer: React.FC<
  ChemicalFormShowEditInventoryContainerProps
> = ({ chemical, ...rest }) => (
  <EntityUpdateContainer
    entityName={ProtocolValue.entityName}
    id={chemical.protocol_value.guid}
  >
    {({ update, status }) => (
      <ChemicalFormShowEditInventory
        chemical={chemical}
        update={update}
        status={status}
        {...rest}
      />
    )}
  </EntityUpdateContainer>
);
